import { useEffect, useState } from "react";
import { Ship, Check, X } from "lucide-react";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useYacht } from "../context/YachtContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";

const ROLE_LABELS = {
  owner: "Owner",
  manager: "Manager",
  captain: "Captain",
  first_officer: "First Officer",
  engineer: "Engineer",
  accountant: "Accountant",
  deckhand: "Deckhand",
  stewardess: "Stewardess",
  chef: "Chef",
  hostess: "Hostess",
  cleaner: "Cleaner",
  detailer: "Detailer",
  delivery_crew: "Delivery Crew",
  night_watch: "Night Watch",
  maintenance_helper: "Maintenance Helper",
  provider: "Online Supplier",
};

/**
 * Shown automatically after the user signs in if they have any unconfirmed
 * yacht_members rows. The user must explicitly Accept or Decline each
 * invitation before they get access to the yacht.
 */
export default function InvitationModal() {
  const { user } = useAuth();
  const { refresh: refreshYachts } = useYacht();
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!user) { setQueue([]); return; }
    try {
      const { data } = await api.get("/invitations/pending");
      setQueue(data || []);
    } catch { /* non-fatal */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.id]);

  // Refresh when the tab regains focus instead of polling on a timer.
  // Covers the "captain just invited me mid-session" case without any
  // background network noise — the user only sees an invitation appear when
  // they're actively looking at the tab, never as a surprise refetch.
  useEffect(() => {
    if (!user) return;
    const onFocus = () => { load(); };
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line
  }, [user?.id]);

  if (!queue.length) return null;
  const current = queue[0];

  const accept = async () => {
    setBusy(true);
    try {
      await api.post(`/yacht-members/${current.id}/accept`);
      toast.success(`Welcome aboard ${current.yacht_name} — you're now a ${ROLE_LABELS[current.role] || current.role}.`);
      setQueue((q) => q.slice(1));
      refreshYachts();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    setBusy(true);
    try {
      await api.post(`/yacht-members/${current.id}/decline`);
      toast.info(`Declined invitation to ${current.yacht_name}.`);
      setQueue((q) => q.slice(1));
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={() => { /* not dismissable — user must accept or decline */ }}>
      <DialogContent
        className="max-w-md [&>button:last-of-type]:hidden"
        data-testid="invitation-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-zinc-900 text-white">
              <Ship className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle>Crew invitation</DialogTitle>
              <DialogDescription className="text-xs">
                {queue.length > 1
                  ? `${queue.length} invitations awaiting your response`
                  : "Awaiting your response"}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-3 text-sm leading-relaxed">
          <p>
            <strong>{current.inviter_name}</strong> has invited you to join the crew of{" "}
            <strong>{current.yacht_name}</strong> as{" "}
            <strong>{ROLE_LABELS[current.role] || current.role}</strong>.
          </p>
          <p className="text-zinc-600 text-[13px]">
            Do you want to be part of the crew? You can decline now or accept and start
            working on this yacht right away. Your Seavanto account stays the same either way.
          </p>
        </div>

        <DialogFooter className="flex sm:justify-between gap-2 mt-2">
          <Button
            type="button"
            variant="outline"
            onClick={decline}
            disabled={busy}
            data-testid="invitation-decline"
          >
            <X className="h-4 w-4 mr-2" />
            Decline
          </Button>
          <Button
            type="button"
            onClick={accept}
            disabled={busy}
            className="bg-ink hover:bg-black"
            data-testid="invitation-accept"
          >
            <Check className="h-4 w-4 mr-2" />
            Yes, join the crew
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
